import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Car, Truck, Bike, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { vehicleApi, VehicleProfile } from '../lib/api';
import { useAuth } from '../context/AuthContext';

interface MyVehiclePageProps {
  onBack: () => void;
  onRequireAuth: () => void;
}

const VEHICLE_OPTIONS = [
  { id: 'auto', label: 'Auto', icon: Car },
  { id: 'suv', label: 'SUV', icon: Car },
  { id: 'camioneta', label: 'Camioneta', icon: Truck },
  { id: 'carga', label: 'Carga', icon: Truck },
  { id: 'van', label: 'Van', icon: Truck },
  { id: 'auto_remolque', label: 'Auto con remolque', icon: Car },
  { id: 'moto', label: 'Moto', icon: Bike },
] as const;

const FUEL_OPTIONS = [
  { id: 'gasoline', label: 'Bencina' },
  { id: 'diesel', label: 'Diésel' },
  { id: 'electric', label: 'Eléctrico' },
  { id: 'hybrid', label: 'Híbrido' },
] as const;

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const MyVehiclePage: React.FC<MyVehiclePageProps> = ({ onBack, onRequireAuth }) => {
  const { user } = useAuth();
  const [vehicleType, setVehicleType] = useState<string>('');
  const [fuelType, setFuelType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSaved = useRef<VehicleProfile>({ vehicle_type: null, fuel_type: null });

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    vehicleApi.get()
      .then((data) => {
        setVehicleType(data.vehicle_type ?? '');
        setFuelType(data.fuel_type ?? '');
        lastSaved.current = data;
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const scheduleAutoSave = (newVehicleType: string, newFuelType: string) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);

    if (
      newVehicleType === (lastSaved.current.vehicle_type ?? '') &&
      newFuelType === (lastSaved.current.fuel_type ?? '')
    ) {
      setSaveStatus('idle');
      return;
    }

    setSaveStatus('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        const updated = await vehicleApi.update({
          vehicle_type: newVehicleType || null,
          fuel_type: newFuelType || null,
        });
        lastSaved.current = updated;
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch {
        setSaveStatus('error');
      }
    }, 400);
  };

  const handleVehicleType = (id: string) => {
    setVehicleType(id);
    scheduleAutoSave(id, fuelType);
  };

  const handleFuelType = (id: string) => {
    setFuelType(id);
    scheduleAutoSave(vehicleType, id);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium mb-6"
        >
          <ArrowLeft size={18} />
          Volver
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-gray-900">Mi vehículo</h1>
            <p className="text-gray-500 mt-1">Personaliza tu perfil de viaje</p>
          </div>
          <div className="h-8 flex items-center">
            {saveStatus === 'saving' && (
              <span className="flex items-center gap-1.5 text-sm text-gray-400">
                <Loader2 size={14} className="animate-spin" /> Guardando…
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1.5 text-sm text-green-600">
                <CheckCircle size={14} /> Guardado
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="flex items-center gap-1.5 text-sm text-red-500">
                <AlertCircle size={14} /> Error al guardar
              </span>
            )}
          </div>
        </div>

        {!user ? (
          <div className="bg-white rounded-3xl border border-gray-100 p-12 text-center shadow-sm">
            <Car className="mx-auto text-gray-300 mb-4" size={48} />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Inicia sesión para continuar</h2>
            <p className="text-gray-500 mb-6">Necesitas una cuenta para guardar la información de tu vehículo.</p>
            <button
              onClick={onRequireAuth}
              className="px-8 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors"
            >
              Iniciar sesión
            </button>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={32} className="animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Tipo de vehículo */}
            <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
              <h2 className="text-base font-bold text-gray-900 mb-4">Tipo de vehículo</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {VEHICLE_OPTIONS.map(({ id, label, icon: Icon }) => {
                  const selected = vehicleType === id;
                  return (
                    <button
                      key={id}
                      onClick={() => handleVehicleType(id)}
                      className={`flex flex-col items-center justify-center gap-2 h-24 rounded-2xl border-2 transition-all font-medium text-sm ${
                        selected
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-blue-200 hover:bg-blue-50/50'
                      }`}
                    >
                      <Icon size={24} className={selected ? 'text-blue-600' : 'text-gray-400'} />
                      {label}
                      {selected && <CheckCircle size={14} className="text-blue-600 absolute" style={{ display: 'none' }} />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tipo de combustible */}
            <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
              <h2 className="text-base font-bold text-gray-900 mb-4">Combustible</h2>
              <div className="grid grid-cols-2 gap-3">
                {FUEL_OPTIONS.map(({ id, label }) => {
                  const selected = fuelType === id;
                  return (
                    <button
                      key={id}
                      onClick={() => handleFuelType(id)}
                      className={`py-3 px-4 rounded-xl border-2 font-semibold text-sm transition-all ${
                        selected
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-blue-200 hover:bg-blue-50/50'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="text-xs text-gray-400 text-center">
              Los cambios se guardan automáticamente
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
